import { useRef, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { UploadSimple, ArrowsClockwise, DownloadSimple, X } from '@phosphor-icons/react'

import { detectFile } from '../../helpers/fileValidation'
import { getAssetLink } from '../../helpers/get-asset-link'
import { downloadFile } from '../../helpers/helpers'

const ONE_MB = 1000000 // 1MB
const DEFAULT_MAX_SIZE = 5 * ONE_MB // 5MB
const DEFAULT_MAX_FILES = 5

const CONFIGS = {
  pdf: {
    extensions: ['pdf'],
    accept: 'application/pdf',
    ignoreCheck: false,
  },
  doc: {
    extensions: ['doc', 'docx'],
    accept:
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/msword',
    ignoreCheck: true,
  },
  image: {
    extensions: ['jpg', 'png', 'jpeg'],
    accept: 'image/png, image/jpeg, image/jpg',
    ignoreCheck: false,
  },
  audio: {
    extensions: ['mp3', 'mp4'],
    accept: 'audio/mp3, audio/mpeg, audio/mp4',
    ignoreCheck: false,
  },
}

const MediaUpload = ({
  onUpdate,
  files = [],
  showIcon = true,
  mediaTypes = ['pdf', 'doc'],
  buttonText = 'Upload',
  width = '200px',
  maxSize = DEFAULT_MAX_SIZE,
  maxFiles = DEFAULT_MAX_FILES,
  labelText,
  multiple = false,
  variant = 'primary',
  disabled = false,
  onRemove,
  downloadAssetPath,
  persistedFileLabel,
  transformFile,
}) => {
  const [error, setError] = useState('')
  const inputRef = useRef(null)

  const filteredFiles = useMemo(() => {
    return files.filter((file) => file instanceof File || typeof file === 'string')
  }, [files])

  const getFileName = (file) => {
    if (file instanceof File) {
      return file.name
    }
    return file
  }

  // Newly-selected files expose their real name; persisted files are stored under a
  // generated (uuid) filename, so fall back to a friendly label when provided.
  const getDisplayName = (file) => {
    if (file instanceof File) {
      return file.name
    }
    return persistedFileLabel || file
  }

  // Shorten long names with a middle ellipsis (keeps the extension visible), so the name
  // never crowds the actions or overflows the container regardless of available width.
  const truncateMiddle = (value, maxLength = 28) => {
    if (!value || value.length <= maxLength) {
      return value
    }
    const front = Math.ceil((maxLength - 1) / 2)
    const back = Math.floor((maxLength - 1) / 2)
    return `${value.slice(0, front)}…${value.slice(value.length - back)}`
  }

  const handleDownload = async (file) => {
    if (file instanceof File) {
      downloadFile(file, file.name)
      return
    }
    const url = /^(https?:|data:)/.test(file)
      ? file
      : getAssetLink({ path: downloadAssetPath, file })
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      downloadFile(blob, getDisplayName(file))
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  const checkFile = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onloadend = (e) => {
        if (e.target.readyState === FileReader.DONE) {
          const fileInfo = detectFile(new Uint8Array(e.target.result))
          const isValidType = mediaTypes.some(
            (type) =>
              CONFIGS[type] &&
              ((fileInfo && CONFIGS[type].extensions.includes(fileInfo.extension)) ||
                CONFIGS[type].ignoreCheck),
          )
          resolve(isValidType)
        }
      }
      reader.onerror = () => resolve(false)
      reader.readAsArrayBuffer(file.slice(0, 4096)) // Read only header 4KB
    })
  }

  const validateFiles = async (selectedFiles) => {
    const validFiles = []
    const errors = []

    for (const selectedFile of selectedFiles) {
      if (selectedFile.size > maxSize) {
        errors.push(`${selectedFile.name}: File size exceeds ${maxSize / ONE_MB}MB`)
        continue
      }

      const file = transformFile ? await transformFile(selectedFile) : selectedFile
      const isValidFile = await checkFile(file)

      if (!isValidFile) {
        errors.push(`${file.name}: Unsupported file type`)
        continue
      }

      validFiles.push(file)
    }

    if (errors.length) {
      setError(errors.join('. '))
    } else {
      setError('')
      onUpdate(multiple ? validFiles : validFiles[0])
    }
  }

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files).slice(0, multiple ? maxFiles : 1)
    validateFiles(files)
  }

  const acceptedTypes = mediaTypes
    .filter((type) => CONFIGS[type])
    .map((type) => CONFIGS[type].accept)
    .join(', ')

  const hasFile = filteredFiles.length > 0 && Boolean(filteredFiles[0])
  const showFileActions = Boolean(onRemove) && !multiple && hasFile

  return (
    <div className="media-upload-wrapper">
      {labelText && <label>{labelText}</label>}
      <input
        ref={inputRef}
        accept={acceptedTypes}
        type="file"
        hidden
        multiple={multiple}
        onChange={handleFileChange}
      />
      <button
        onClick={() => inputRef.current.click()}
        disabled={disabled}
        type="button"
        className={`button button-${variant}`}
        style={{ width }}
      >
        {filteredFiles.length > 0 ? (
          <>
            {showIcon && <ArrowsClockwise size={20} fill="#ffffff" />}
            {`Replace file${filteredFiles.length > 1 ? 's' : ''}`}
          </>
        ) : (
          <>
            {showIcon && <UploadSimple size={20} fill="#ffffff" />}
            {buttonText}
          </>
        )}
      </button>
      {error ? <div className="message error">{error}</div> : null}
      {!error && showFileActions ? (
        <div className="media-upload-file">
          <span className="media-upload-file__name" title={getDisplayName(filteredFiles[0])}>
            {truncateMiddle(getDisplayName(filteredFiles[0]))}
          </span>
          <div className="media-upload-file__actions">
            <button
              type="button"
              className="media-upload-file__action"
              aria-label="Download file"
              onClick={() => handleDownload(filteredFiles[0])}
            >
              <DownloadSimple size={20} />
            </button>
            <button
              type="button"
              className="media-upload-file__action"
              aria-label="Remove file"
              onClick={onRemove}
            >
              <X size={20} />
            </button>
          </div>
        </div>
      ) : null}
      {!error && !showFileActions && hasFile ? (
        <div className="message success">
          {multiple
            ? `${filteredFiles.length} file(s) uploaded`
            : `File ${getFileName(filteredFiles[0])} uploaded`}
        </div>
      ) : null}
    </div>
  )
}

MediaUpload.propTypes = {
  onUpdate: PropTypes.func.isRequired,
  files: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.arrayOf(PropTypes.string),
  ]),
  buttonText: PropTypes.string,
  showIcon: PropTypes.bool,
  mediaTypes: PropTypes.arrayOf(PropTypes.oneOf(['pdf', 'doc', 'image', 'audio'])),
  labelText: PropTypes.string,
  maxSize: PropTypes.number,
  maxFiles: PropTypes.number,
  multiple: PropTypes.bool,
  width: PropTypes.string,
  variant: PropTypes.oneOf(['primary', 'lightgray']),
  disabled: PropTypes.bool,
  onRemove: PropTypes.func,
  downloadAssetPath: PropTypes.string,
  persistedFileLabel: PropTypes.string,
  transformFile: PropTypes.func,
}

export default MediaUpload
